import { Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import TemplatesPage from './pages/TemplatesPage';
import DocsPage from './pages/DocsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import HostingGetStartedPage from './pages/HostingGetStartedPage';
import MockupGallery from './pages/MockupGallery';
import ConciergeView from './pages/mockups/ConciergeView';
import StudioView from './pages/mockups/StudioView';
import CanvasView from './pages/mockups/CanvasView';
import FlowView from './pages/mockups/FlowView';
import ThreadView from './pages/mockups/ThreadView';
import UnifiedView from './pages/mockups/UnifiedView';

// Preview Mode Context
interface PreviewContextType {
  isPreviewMode: boolean;
  setPreviewMode: (mode: boolean) => void;
  exitPreview: () => void;
}

const PreviewContext = createContext<PreviewContextType>({
  isPreviewMode: false,
  setPreviewMode: () => {},
  exitPreview: () => {},
});

export const usePreview = () => useContext(PreviewContext);

function PreviewProvider({ children }: { children: React.ReactNode }) {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasPreviewParam = urlParams.get('preview') === 'true';
    const hasPreviewCookie = document.cookie.includes('ww_preview=1');
    const hasToken = localStorage.getItem('ww_token') || localStorage.getItem('token');

    if (hasPreviewParam || hasPreviewCookie || hasToken) {
      setIsPreviewMode(true);
      if (hasPreviewParam) {
        document.cookie = 'ww_preview=1; path=/; max-age=86400';
      }
    }
    setIsLoading(false);
  }, []);

  const setPreviewMode = (mode: boolean) => {
    setIsPreviewMode(mode);
    if (mode) {
      document.cookie = 'ww_preview=1; path=/; max-age=86400';
    } else {
      document.cookie = 'ww_preview=; path=/; max-age=0';
    }
  };

  const exitPreview = () => {
    document.cookie = 'ww_preview=; path=/; max-age=0';
    setIsPreviewMode(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <PreviewContext.Provider value={{ isPreviewMode, setPreviewMode, exitPreview }}>
      {children}
    </PreviewContext.Provider>
  );
}

// Preview Admin Bar
function PreviewBar() {
  const { isPreviewMode, exitPreview } = usePreview();
  
  if (!isPreviewMode) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-emerald-600/95 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-white font-medium">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Construction Mode — Full site preview
          </span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://dashboard.wiserwebcloud.com/"
            className="text-xs text-white/80 hover:text-white transition flex items-center gap-1"
          >
            <span className="material-icons text-sm">dashboard</span>
            Dashboard
          </a>
          <button
            onClick={exitPreview}
            className="text-xs text-white/80 hover:text-white transition flex items-center gap-1 px-2 py-1 bg-white/10 rounded"
          >
            <span className="material-icons text-sm">visibility_off</span>
            View as Public
          </button>
        </div>
      </div>
    </div>
  );
}

// Coming Soon page for public visitors
function ComingSoonPage() {
  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Admin Login Link */}
      <a
        href="https://dashboard.wiserwebcloud.com/"
        className="absolute top-6 right-6 z-50 flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-orange-400 transition-all bg-zinc-900/70 backdrop-blur-sm rounded-lg border border-zinc-700 hover:border-orange-500/50 hover:bg-zinc-800/70"
      >
        <span className="material-icons text-lg">dashboard</span>
        Dashboard
      </a>

      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-red-500/10" />
      <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-orange-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-red-500/20 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col xl:flex-row gap-12 xl:gap-16 items-center">
          {/* Owl Mascot */}
          <div className="flex justify-center xl:justify-end w-full xl:w-1/2 order-2 xl:order-1">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-full blur-3xl scale-75" />
              <svg
                className="w-56 sm:w-72 md:w-80 xl:w-96 h-56 sm:h-72 md:h-80 xl:h-96 relative z-10"
                viewBox="0 0 120 120"
              >
                <defs>
                  <linearGradient id="bigOwlGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#f97316' }} />
                    <stop offset="100%" style={{ stopColor: '#ea580c' }} />
                  </linearGradient>
                  <linearGradient id="beakGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#fbbf24' }} />
                    <stop offset="100%" style={{ stopColor: '#f59e0b' }} />
                  </linearGradient>
                </defs>
                <ellipse cx="60" cy="65" rx="45" ry="42" fill="url(#bigOwlGrad)" />
                <polygon points="22,35 32,18 40,38" fill="url(#bigOwlGrad)" />
                <polygon points="98,35 88,18 80,38" fill="url(#bigOwlGrad)" />
                <ellipse cx="60" cy="75" rx="25" ry="22" fill="#fdba74" opacity="0.3" />
                <circle cx="42" cy="55" r="18" fill="#18181b" />
                <circle cx="42" cy="55" r="12" fill="white" />
                <circle cx="45" cy="52" r="6" fill="#18181b" />
                <circle cx="47" cy="50" r="2" fill="white" />
                <circle cx="78" cy="55" r="18" fill="#18181b" />
                <circle cx="78" cy="55" r="12" fill="white" />
                <circle cx="81" cy="52" r="6" fill="#18181b" />
                <circle cx="83" cy="50" r="2" fill="white" />
                <polygon points="60,68 52,82 60,90 68,82" fill="url(#beakGrad)" />
                <ellipse cx="45" cy="105" rx="8" ry="4" fill="url(#beakGrad)" />
                <ellipse cx="75" cy="105" rx="8" ry="4" fill="url(#beakGrad)" />
              </svg>
            </div>
          </div>

          {/* Content */}
          <div className="text-center xl:text-left w-full xl:w-1/2 order-1 xl:order-2">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              Coming{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                Soon
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-zinc-400 mb-3">
              We're building something special.
            </p>
            <p className="text-base sm:text-lg text-zinc-500 mb-8 max-w-md mx-auto xl:mx-0">
              A new way to deploy your projects with AI-powered simplicity. Stay tuned for our
              launch.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center xl:justify-start gap-4">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
              >
                <span className="material-icons text-lg">mail</span>
                Contact Us
              </a>
              <span className="text-sm text-zinc-500">Questions? Get in touch</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-zinc-800 text-center">
          <p className="text-sm text-zinc-600">© 2025&ndash;{new Date().getFullYear()} Wiser Web Solutions Inc. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const { isPreviewMode } = usePreview();
  const location = useLocation();
  
  // Mockup pages bypass preview mode and render standalone (no Navbar/Footer)
  if (location.pathname.startsWith('/mockups')) {
    return (
      <div className="min-h-screen bg-base">
        <Routes>
          <Route path="/mockups" element={<MockupGallery />} />
          <Route path="/mockups/concierge" element={<ConciergeView />} />
          <Route path="/mockups/studio" element={<StudioView />} />
          <Route path="/mockups/canvas" element={<CanvasView />} />
          <Route path="/mockups/flow" element={<FlowView />} />
          <Route path="/mockups/thread" element={<ThreadView />} />
          <Route path="/mockups/unified" element={<UnifiedView />} />
        </Routes>
      </div>
    );
  }

  // Pages that should show for public (even in construction mode)
  const publicPages = ['/contact'];
  const isPublicPage = publicPages.includes(location.pathname);

  // Show Coming Soon for non-preview users (except public pages)
  if (!isPreviewMode && !isPublicPage) {
    return <ComingSoonPage />;
  }

  return (
    <div className="min-h-screen bg-base flex flex-col">
      <PreviewBar />
      <Navbar />
      <main className={`flex-1 ${isPreviewMode ? 'pt-8' : ''}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/hosting" element={<HostingGetStartedPage />} />
          {/* Fallback */}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <PreviewProvider>
      <AppContent />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#18181b',
            color: '#fafafa',
            border: '1px solid #27272a',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#18181b',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#18181b',
            },
          },
        }}
      />
    </PreviewProvider>
  );
}

export default App;
